/**
 * Tenant legal document overrides (settings + public resolve).
 * When a block is enabled, all required fields must be filled for ru/en/he — no platform fallback.
 */

export const LEGAL_DOCUMENT_KEYS = [
  'terms',
  'privacy',
  'accessibility',
  'cancel',
  'cookie',
] ;

export const COOKIE_COPY_KEYS = [
  'bannerText',
  'agree',
  'disagree',
  'leaveText',
  'privacyNote',
  'privacyCheckbox',
  'privacySaved',
];

const LANGS = ['ru', 'en', 'he'];

export function emptyI18nTriple() {
  return { ru: '', en: '', he: '' };
}

function normalizeTriple(raw) {
  return {
    ru: typeof raw?.ru === 'string' ? raw.ru : '',
    en: typeof raw?.en === 'string' ? raw.en : '',
    he: typeof raw?.he === 'string' ? raw.he : '',
  };
}

function tripleFilled(triple) {
  return LANGS.every((code) => String(triple?.[code] || '').trim().length > 0);
}

function emptyDocBlock() {
  return {
    enabled: false,
    title: emptyI18nTriple(),
    body: emptyI18nTriple(),
  };
}

function emptyCookieBlock() {
  const block = { enabled: false };
  for (const key of COOKIE_COPY_KEYS) {
    block[key] = emptyI18nTriple();
  }
  return block;
}

function normalizeDocBlock(raw) {
  return {
    enabled: raw?.enabled === true,
    title: normalizeTriple(raw?.title),
    body: normalizeTriple(raw?.body),
  };
}

function normalizeCookieBlock(raw) {
  const block = { enabled: raw?.enabled === true };
  for (const key of COOKIE_COPY_KEYS) {
    block[key] = normalizeTriple(raw?.[key]);
  }
  return block;
}

/** @returns {import('../types').TenantLegalDocuments} */
export function normalizeLegalDocuments(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      terms: emptyDocBlock(),
      privacy: emptyDocBlock(),
      accessibility: emptyDocBlock(),
      cancel: emptyDocBlock(),
      cookie: emptyCookieBlock(),
    };
  }
  return {
    terms: normalizeDocBlock(raw.terms),
    privacy: normalizeDocBlock(raw.privacy),
    accessibility: normalizeDocBlock(raw.accessibility),
    cancel: normalizeDocBlock(raw.cancel),
    cookie: normalizeCookieBlock(raw.cookie),
  };
}

/**
 * @param {ReturnType<typeof normalizeLegalDocuments>} docs
 * @returns {string | null} error code
 */
export function validateLegalDocuments(docs) {
  const normalized = normalizeLegalDocuments(docs);

  for (const key of ['terms', 'privacy', 'accessibility', 'cancel']) {
    const block = normalized[key];
    if (!block.enabled) continue;
    if (!tripleFilled(block.title) || !tripleFilled(block.body)) {
      return 'legal_incomplete';
    }
  }

  if (normalized.cookie.enabled) {
    for (const field of COOKIE_COPY_KEYS) {
      if (!tripleFilled(normalized.cookie[field])) {
        return 'legal_incomplete';
      }
    }
  }

  return null;
}

export function pickI18n(triple, lang) {
  if (lang === 'uk' || lang === 'ka') {
    return String(triple?.[lang] || '').trim();
  }
  const code = LANGS.includes(lang) ? lang : 'en';
  return String(triple?.[code] || '').trim();
}

/**
 * Resolve a legal page (terms / privacy / accessibility / cancel).
 * @returns {{ mode: 'platform' } | { mode: 'custom', title: string, body: string }}
 */
export function resolveLegalPageCopy(docs, pageKey, lang) {
  const normalized = normalizeLegalDocuments(docs);
  const block = normalized[pageKey];
  if (!block || !block.enabled) {
    return { mode: 'platform' };
  }
  return {
    mode: 'custom',
    title: pickI18n(block.title, lang),
    body: pickI18n(block.body, lang),
  };
}

/**
 * Resolve one cookie copy key.
 * @returns {{ mode: 'platform' } | { mode: 'custom', text: string }}
 */
export function resolveCookieCopy(docs, fieldKey, lang) {
  const normalized = normalizeLegalDocuments(docs);
  if (!normalized.cookie.enabled) {
    return { mode: 'platform' };
  }
  return {
    mode: 'custom',
    text: pickI18n(normalized.cookie[fieldKey], lang),
  };
}

/** Platform i18n keys for «insert current» (document title/body). */
export const LEGAL_PLATFORM_I18N_KEYS = {
  terms: { title: 'legal.terms.title', body: 'legal.terms.body' },
  privacy: { title: 'legal.privacy.title', body: 'legal.privacy.body' },
  accessibility: { title: 'legal.accessibility.title', body: 'legal.accessibility.body' },
  cancel: { title: 'legal.cancel.title', body: 'legal.cancel.intro' },
};

export const COOKIE_PLATFORM_I18N_KEYS = {
  bannerText: 'cookie.bannerText',
  agree: 'cookie.agree',
  disagree: 'cookie.disagree',
  leaveText: 'cookie.leaveText',
  privacyNote: 'cookie.privacyNote',
  privacyCheckbox: 'cookie.privacyCheckbox',
  privacySaved: 'cookie.privacySaved',
};
